import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CloudUpload,
  ArrowBack,
  Assessment,
  InfoOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { contractsApi, costsApi } from '../../services/benchmarkApi';
import BenchmarkUploadDialog from './BenchmarkUploadDialog';
import BenchmarkAiTransformDialog from './BenchmarkAiTransformDialog';
import BenchmarkExtraDataDrawer from './BenchmarkExtraDataDrawer';
import { useBenchmarkGridView } from './useBenchmarkGridView';
import { BenchmarkGridToolbar } from './BenchmarkGridToolbar';
import { formatMoney, formatNumber } from './benchmarkFormat';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
});

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');
const toNum = (value) => (value != null && value !== '' ? Number(value) : null);

const mapEstimateRow = (t) => ({
  id: t.costTransactionId || t.id,
  structureCode: t.structureCode || '',
  boqItemNumber: t.boqItemNumber || '',
  description: t.description || '',
  unit: t.unit || '',
  quantity: toNum(t.quantity),
  unitPrice: toNum(t.unitPrice),
  amount: toNum(t.amount),
  laborCost: toNum(t.laborCost),
  materialCost: toNum(t.materialCost),
  equipmentCost: toNum(t.equipmentCost),
  subcontractCost: toNum(t.subcontractCost),
  transactionDate: formatDate(t.transactionDate),
  transactionType: t.transactionType || '',
  extraJson: t.extraJson || null,
  hasExtra: Boolean(t.extraJson),
});

const BenchmarkEstimatedBudgets = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const location = useLocation();
  const contract = location.state?.contract;
  const project = location.state?.project;

  const [projectId, setProjectId] = useState(
    project?.id || project?.projectId || contract?.projectId || null
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [aiUploadOpen, setAiUploadOpen] = useState(false);
  const [extraDrawerOpen, setExtraDrawerOpen] = useState(false);
  const [extraDrawerRow, setExtraDrawerRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const resolveProjectId = async () => {
      const fromState = project?.id || project?.projectId || contract?.projectId || null;
      if (fromState) {
        if (!cancelled) setProjectId(fromState);
        return;
      }
      if (!contractId) return;
      try {
        const res = await contractsApi.getById(contractId);
        if (!cancelled) setProjectId(res.data?.projectId || null);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || 'Failed to resolve project for contract');
          setProjectId(null);
        }
      }
    };
    resolveProjectId();
    return () => { cancelled = true; };
  }, [project?.id, project?.projectId, contract?.projectId, contractId]);

  const loadRows = useCallback(async () => {
    if (!projectId || !contractId) {
      setRows([]);
      if (!projectId) {
        setLoadError((prev) => prev || 'Project context is required to load estimated budgets');
      }
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [estimateRes, boqRes] = await Promise.all([
        costsApi.transactions(projectId, { transactionType: 'ESTIMATE', contractId }),
        costsApi.transactions(projectId, { transactionType: 'BOQ', contractId }),
      ]);
      const merged = [...(estimateRes.data || []), ...(boqRes.data || [])];
      const seen = new Set();
      const mapped = [];
      merged.forEach((t) => {
        const id = t.costTransactionId || t.id;
        if (seen.has(id)) return;
        seen.add(id);
        mapped.push(mapEstimateRow(t));
      });
      setRows(mapped);
    } catch (e) {
      setLoadError(e.message || 'Failed to load estimate lines');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, contractId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const baseColumns = useMemo(
    () => [
      { field: 'structureCode', headerName: 'Structure Code', width: 140 },
      { field: 'boqItemNumber', headerName: 'BoQ Item', width: 120 },
      { field: 'description', headerName: 'Description', width: 260, minWidth: 200 },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 100,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatNumber(v, 2),
      },
      { field: 'unit', headerName: 'Unit', width: 90 },
      {
        field: 'unitPrice',
        headerName: 'Unit Price',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      {
        field: 'laborCost',
        headerName: 'Labor',
        width: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      {
        field: 'materialCost',
        headerName: 'Material',
        width: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      {
        field: 'equipmentCost',
        headerName: 'Equipment',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      {
        field: 'subcontractCost',
        headerName: 'Subcontract',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => formatMoney(v),
      },
      { field: 'transactionDate', headerName: 'Date', width: 120 },
      {
        field: 'hasExtra',
        headerName: 'Others',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.hasExtra ? (
            <Tooltip title="View other (unmapped) Excel columns">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExtraDrawerRow(params.row);
                  setExtraDrawerOpen(true);
                }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null,
      },
    ],
    []
  );

  const {
    columns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    quickFilter,
    setQuickFilter,
    filterModel,
    onFilterModelChange,
    openCustomize,
    CustomizeDialog,
  } = useBenchmarkGridView('benchmark_estimate', baseColumns);

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/benchmark/contracts/${projectId || project?.id}`, {
                    state: { contract, project: project || { id: projectId } }
                  })}
                  sx={{ mr: 1 }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Title variant="h6">
                  <Assessment fontSize="small" />
                  Estimate / BoQ
                  {contract && (
                    <Typography variant="body2" sx={{ ml: 2, fontWeight: 400, color: 'text.secondary' }}>
                      - {contract.contractCode}
                    </Typography>
                  )}
                </Title>
              </Box>
              <ButtonGroup>
                <Tooltip title="Upload Excel">
                  <IconButton
                    color="primary"
                    onClick={() => setShowUploadDialog(true)}
                    size="small"
                  >
                    <CloudUpload />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            </TitleBar>
            {loadError && (
              <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setLoadError('')}>
                {loadError}
              </Alert>
            )}
          </HeaderContainer>

          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <StyledDataGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              filterMode="client"
              filterDebounceMs={200}
              slots={{ toolbar: BenchmarkGridToolbar }}
              slotProps={{
                toolbar: {
                  quickFilterValue: quickFilter,
                  onQuickFilterChange: setQuickFilter,
                  onOpenCustomize: openCustomize,
                },
              }}
              filterModel={filterModel}
              onFilterModelChange={onFilterModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              disableRowSelectionOnClick
              density="compact"
              autoHeight={false}
              headerHeight={48}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      {CustomizeDialog}

      <BenchmarkUploadDialog
        open={showUploadDialog}
        uploadType="estimate"
        defaultProjectId={projectId}
        defaultContractId={contractId}
        lockContext
        onClose={() => setShowUploadDialog(false)}
        onOpenAiUpload={() => {
          setShowUploadDialog(false);
          setAiUploadOpen(true);
        }}
        onSuccess={() => {
          loadRows();
        }}
      />
      <BenchmarkAiTransformDialog
        open={aiUploadOpen}
        onClose={() => setAiUploadOpen(false)}
        defaultProjectId={projectId}
        onSuccess={() => {
          setAiUploadOpen(false);
          loadRows();
        }}
      />
      <BenchmarkExtraDataDrawer
        open={extraDrawerOpen}
        onClose={() => {
          setExtraDrawerOpen(false);
          setExtraDrawerRow(null);
        }}
        row={extraDrawerRow}
      />
    </RootDiv>
  );
};

export default BenchmarkEstimatedBudgets;
